// useCounselorNotifications — loads + streams draft_comparison notifications for the logged-in counselor | drives the notification bell badge count

import { useEffect, useState, useCallback } from 'react'
import { UserProfile } from '../context/ProfileContext'
import {
  getCounselorNotifications,
  markCounselorNotificationRead,
  markAllCounselorNotificationsRead,
  subscribeToCounselorNotifications,
  type StudentNotification,
} from '../services/notificationsApi'

export function useCounselorNotifications() {
  const profileContext = UserProfile()
  const counselorId = profileContext?.profile?.id ?? null

  const [notifications, setNotifications] = useState<StudentNotification[]>([])
  const [loading, setLoading] = useState(false)

  const unreadCount = notifications.filter((n) => !n.is_read).length

  // Initial load
  useEffect(() => {
    if (!counselorId) return
    setLoading(true)
    getCounselorNotifications(counselorId)
      .then(setNotifications)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [counselorId])

  // Realtime: prepend new notifications as they arrive
  useEffect(() => {
    if (!counselorId) return
    const unsubscribe = subscribeToCounselorNotifications(counselorId, (newNotif) => {
      setNotifications((prev) => [newNotif, ...prev])
    })
    return () => { unsubscribe() }
  }, [counselorId])

  const markRead = useCallback(async (notificationId: string) => {
    await markCounselorNotificationRead(notificationId)
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
      ),
    )
  }, [])

  const markAllRead = useCallback(async () => {
    if (!counselorId) return
    await markAllCounselorNotificationsRead(counselorId)
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
    )
  }, [counselorId])

  return { notifications, unreadCount, loading, markRead, markAllRead }
}
